import {
  COOKS_MAP_KEY,
  scaleIngredientForDisplay,
  type CookLogEntry,
  type UnitSystem,
} from '@awwyeah/shared';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CookingPot,
  Pause,
  Play,
  RotateCcw,
  Timer,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  cancelTimer,
  EMPTY_TIMERS_STATE,
  formatCountdown,
  pauseTimer,
  remainingSeconds,
  resumeTimer,
  startTimer,
  tick,
  type CookTimer,
  type CookTimersState,
} from '../cookTimers';
import { CookSummaryCard } from '../components/CookSummaryCard';
import { parseCookSettings } from '../utils/cookSettings';
import { ensureStructured } from '../utils/ensureStructured';
import { readStoredScale, resolveCookFactor, writeStoredScale } from '../utils/scalePreference';
import { newId } from '../utils/id';
import { parseDurations } from '../utils/parseDurations';
import { notifyTimerDone, playChime } from '../utils/chime';
import {
  clearCookSession,
  readCookSession,
  writeCookSession,
  type CookSessionState,
} from '../utils/cookSession';
import {
  readStoredUnitsPreference,
  unitsPreferenceToSystem,
  type UnitsPreference,
} from '../utils/unitsPreference';
import { useRecipe, useRecipesDoc } from '../hooks';
import { logCook, useCooksDoc } from '../cooks';
import { useUser } from '../../auth';

/**
 * Best-effort Screen Wake Lock. Deliberately duplicated from `ShoppingModePage`'s
 * `useWakeLock` (apps/web/src/modules/lists/ShoppingModePage.tsx) rather than extracted to
 * a shared hooks location — there isn't one yet (`apps/web/src/hooks/` doesn't exist), and
 * inventing a new cross-module directory for an 18-line hook while three sibling
 * workstreams are running in parallel elsewhere in the tree felt like more risk (a shared
 * file everyone has to agree on) than benefit. If a third module needs this, that's the
 * signal to actually extract it.
 */
function useWakeLock(active: boolean): void {
  const sentinel = useRef<{ release: () => Promise<void> } | null>(null);

  useEffect(() => {
    if (!active) return undefined;
    const nav = navigator as Navigator & {
      wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> };
    };
    if (!nav.wakeLock) return undefined;

    let cancelled = false;
    const acquire = async (): Promise<void> => {
      try {
        const lock = await nav.wakeLock?.request('screen');
        if (lock && !cancelled) sentinel.current = lock;
        else await lock?.release();
      } catch {
        // Wake lock is best-effort — denied/unsupported is silently fine.
      }
    };
    void acquire();

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void acquire();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel.current?.release();
      sentinel.current = null;
    };
  }, [active]);
}

function serializeTimers(state: CookTimersState): CookSessionState['timers'] {
  return state.timers.map((t) => ({
    id: t.id,
    name: t.name,
    durationSeconds: t.durationSeconds,
    status: t.status,
    endTime: t.endTime,
    remainingAtPause: t.remainingAtPause,
  }));
}

function deserializeTimers(saved: CookSessionState['timers']): CookTimersState {
  return {
    timers: saved.map((t) => ({
      id: t.id,
      name: t.name,
      durationSeconds: t.durationSeconds,
      status: t.status,
      endTime: t.endTime,
      remainingAtPause: t.remainingAtPause,
    })),
  };
}

interface LocationCookState {
  factor?: number;
  unitsPreference?: UnitsPreference;
}

/** Per-device memory of whether the phone-width ingredients panel was left open. */
const INGREDIENTS_OPEN_KEY = 'awwyeah:cook:ingredientsOpen';

function readIngredientsOpen(): boolean {
  try {
    return window.localStorage.getItem(INGREDIENTS_OPEN_KEY) !== 'false';
  } catch {
    return true;
  }
}

function writeIngredientsOpen(open: boolean): void {
  try {
    window.localStorage.setItem(INGREDIENTS_OPEN_KEY, String(open));
  } catch {
    // Best-effort — it just opens again next time.
  }
}

/** The timer name a step's duration chip starts — also how a chip finds "its" timer again
 * (including after a reload, since names round-trip through the cook session). */
function timerNameFor(stepNum: number, label: string): string {
  return `Step ${stepNum} · ${label.trim()}`;
}

/**
 * Full-screen cook view (ROADMAP §3 WS-19 — the recipe's "second life"). Mirrors
 * `ShoppingModePage`'s pattern: a `fixed inset-0` layer over the still-mounted app chrome,
 * doing its own safe-area padding and holding a wake lock for as long as it's open.
 *
 * Layout (owner ask 2026-09): a "before you start" summary card on top (method, oven
 * temperature, pan, heat, servings — `CookSummaryCard`), then ingredients and steps *both*
 * visible — stacked on a phone with ingredients as a collapsible panel above the steps,
 * side by side from `md` up. Running timers are pinned under the header, outside the scroll,
 * so a countdown is never scrolled away.
 *
 * Ingredient check-offs and step progress are **ephemeral** — see the docblock on
 * `utils/cookSession.ts` for why this is the one place in the app that deliberately does
 * not sync via Yjs. The scale factor is the per-recipe preference the detail page also reads
 * (`utils/scalePreference.ts`). Timers are a pure `cookTimers.ts` store driven by one
 * `setInterval` here; see that module's docblock for the endTime-based, drift-resistant
 * design and its "open tab only" honest limitation.
 */
export function CookModePage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const doc = useRecipesDoc();
  const recipe = useRecipe(doc, id);
  const cooksDoc = useCooksDoc();
  const { user } = useUser();

  const incoming = (location.state as LocationCookState | null) ?? null;
  const storage = window.localStorage;

  const initialSession = id ? readCookSession(storage, id) : null;

  const [checked, setChecked] = useState<Set<string>>(
    () => new Set(initialSession?.checkedIngredientIds ?? []),
  );
  const [stepIndex, setStepIndex] = useState(initialSession?.currentStepIndex ?? 0);
  const [factor, setFactorState] = useState(() =>
    resolveCookFactor(
      id ? readStoredScale(storage, id) : null,
      incoming?.factor,
      initialSession?.scaleFactor,
    ),
  );
  const [unitsPreference] = useState<UnitsPreference>(
    initialSession?.unitsPreference ??
      incoming?.unitsPreference ??
      readStoredUnitsPreference(storage),
  );
  const [ingredientsOpen, setIngredientsOpen] = useState(readIngredientsOpen);
  const [timersState, setTimersState] = useState<CookTimersState>(() =>
    initialSession ? deserializeTimers(initialSession.timers) : EMPTY_TIMERS_STATE,
  );
  const [, forceTick] = useState(0);
  const [exitConfirm, setExitConfirm] = useState(false);

  useWakeLock(Boolean(recipe));

  // One interval drives the whole timer store — endTime-based, so a throttled background
  // tab can only deliver "done" late, never drift the actual finish time.
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTimersState((prev) => {
        const { state: next, justFinished } = tick(prev, now);
        for (const timer of justFinished) {
          playChime();
          notifyTimerDone(timer.name);
        }
        return next;
      });
      forceTick((n) => n + 1);
    }, 250);
    return () => clearInterval(interval);
  }, []);

  // Persist ephemeral session state on every change (localStorage round-trip).
  useEffect(() => {
    if (!id) return;
    writeCookSession(storage, id, {
      checkedIngredientIds: [...checked],
      currentStepIndex: stepIndex,
      scaleFactor: factor,
      unitsPreference,
      timers: serializeTimers(timersState),
    });
  }, [checked, stepIndex, factor, unitsPreference, timersState, id, storage]);

  const steps = recipe?.steps ?? EMPTY_STEPS;
  const settings = useMemo(() => parseCookSettings(steps), [steps]);

  if (!recipe || !id) {
    return (
      <div className="fixed inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-bg px-6 text-center">
        <p className="text-[15px] text-fg-2">This recipe isn&apos;t here anymore.</p>
        <button
          type="button"
          onClick={() => void navigate('/recipes')}
          className="text-[13px] font-semibold text-accent"
        >
          Back to the box
        </button>
      </div>
    );
  }

  const toSystem: UnitSystem | null = unitsPreferenceToSystem(unitsPreference);
  const hasActiveTimer = timersState.timers.some((t) => t.status === 'running');
  const now = Date.now();

  function setFactor(next: number): void {
    setFactorState(next);
    if (id) writeStoredScale(storage, id, next);
  }

  function requestExit(): void {
    if (hasActiveTimer) {
      setExitConfirm(true);
      return;
    }
    navigate(`/recipes/${id}`);
  }

  function startOver(): void {
    if (!id) return;
    setChecked(new Set());
    setStepIndex(0);
    setTimersState(EMPTY_TIMERS_STATE);
    clearCookSession(storage, id);
  }

  /**
   * "Cooked this", at the end of the steps (owner ask 2026-08-18). This is the honest
   * moment for it — you are standing there having just finished — which is why it lives
   * under the last step rather than in the header, where it would be an invitation to
   * claim a cook you haven't done yet.
   *
   * It logs, clears the now-spent session (a finished cook shouldn't greet you half
   * checked-off next time) and leaves, handing the undo to the detail page via
   * `CookHandoffState`: this full-screen layer unmounts on the way out, so it has nowhere
   * of its own to put a toast that outlives the tap.
   */
  function finishAndLogCook(): void {
    if (!recipe || !id || !user) return;
    const entry = logCook(cooksDoc.getMap<CookLogEntry>(COOKS_MAP_KEY), recipe, user.id);
    clearCookSession(storage, id);
    navigate(`/recipes/${id}`, {
      state: { cookedEntryId: entry.id, cookedTitle: recipe.title },
    });
  }

  function toggleIngredient(ingredientId: string): void {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(ingredientId)) next.delete(ingredientId);
      else next.add(ingredientId);
      return next;
    });
  }

  function toggleIngredientsOpen(): void {
    setIngredientsOpen((open) => {
      writeIngredientsOpen(!open);
      return !open;
    });
  }

  function requestNotificationPermissionOnce(): void {
    try {
      if (typeof Notification === 'undefined') return;
      if (Notification.permission === 'default') void Notification.requestPermission();
    } catch {
      // Best-effort only.
    }
  }

  /**
   * A duration chip's tap. No timer for it yet (or only a finished one): start one. Already
   * running or paused: the chip is that timer's pause/resume, so a second tap never stacks a
   * duplicate countdown for the same step.
   */
  function handleChip(stepNum: number, label: string, seconds: number): void {
    const name = timerNameFor(stepNum, label);
    const existing = timersState.timers.find((t) => t.name === name && t.status !== 'done');
    const t = Date.now();
    if (existing?.status === 'running') {
      setTimersState((prev) => pauseTimer(prev, existing.id, t));
      return;
    }
    if (existing?.status === 'paused') {
      setTimersState((prev) => resumeTimer(prev, existing.id, t));
      return;
    }
    requestNotificationPermissionOnce();
    setTimersState((prev) => {
      const withoutDone = prev.timers.filter((x) => !(x.name === name && x.status === 'done'));
      return startTimer(
        { timers: withoutDone },
        { id: newId(), name, durationSeconds: seconds },
        t,
      );
    });
  }

  const maxStep = Math.max(0, steps.length - 1);
  const clampedStepIndex = Math.min(stepIndex, maxStep);
  const remainingCount = recipe.ingredients.filter((ing) => !checked.has(ing.id)).length;

  return (
    <div className="fixed inset-0 z-30 flex flex-col bg-bg" data-testid="cook-mode-page">
      <header className="flex min-h-[56px] flex-shrink-0 items-center gap-2 border-b-2 border-line-strong bg-surface px-2 pt-[env(safe-area-inset-top)]">
        <button
          type="button"
          aria-label="Exit cook mode"
          data-testid="exit-cook-mode"
          onClick={requestExit}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center"
        >
          <X size={22} />
        </button>
        <h1 className="min-w-0 flex-1 truncate text-[15px] font-bold tracking-tight text-fg">
          {recipe.title}
        </h1>
        <button
          type="button"
          data-testid="cook-start-over"
          onClick={startOver}
          aria-label="Start over"
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center text-fg-2"
        >
          <RotateCcw size={20} />
        </button>
      </header>

      {timersState.timers.length > 0 && (
        <div
          className="flex max-h-[40vh] flex-shrink-0 flex-col gap-1.5 overflow-y-auto border-b-2 border-line-strong bg-raised px-3 py-2"
          data-testid="timer-bar"
          aria-label="Timers"
        >
          {timersState.timers.map((t) => (
            <TimerRow
              key={t.id}
              timer={t}
              now={now}
              onPause={() => setTimersState((prev) => pauseTimer(prev, t.id, Date.now()))}
              onResume={() => setTimersState((prev) => resumeTimer(prev, t.id, Date.now()))}
              onCancel={() => setTimersState((prev) => cancelTimer(prev, t.id))}
            />
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto" data-testid="cook-scroll">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-3 px-4 py-3 md:px-6">
          <CookSummaryCard
            settings={settings}
            unitsPreference={unitsPreference}
            baseServings={recipe.servings}
            factor={factor}
            onFactorChange={setFactor}
            showScale={recipe.ingredients.length > 0}
            onJumpToStep={setStepIndex}
          />

          <div className="flex flex-col gap-3 md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:items-start md:gap-6">
            <section
              className="rounded-lg border border-line bg-surface"
              data-testid="cook-ingredients-panel"
              aria-label="Ingredients"
            >
              <button
                type="button"
                data-testid="cook-ingredients-toggle"
                aria-expanded={ingredientsOpen}
                onClick={toggleIngredientsOpen}
                className="flex h-12 w-full items-center gap-2 px-4 text-left md:pointer-events-none"
              >
                <span className="flex-1 text-[11px] font-semibold uppercase tracking-wider text-fg-3">
                  Ingredients · {recipe.ingredients.length}
                  {checked.size > 0 && remainingCount > 0 && (
                    <span className="normal-case tracking-normal"> · {remainingCount} to go</span>
                  )}
                </span>
                <ChevronDown
                  size={18}
                  aria-hidden="true"
                  className={`text-fg-3 md:hidden ${ingredientsOpen ? 'rotate-180' : ''}`}
                />
              </button>
              <div
                className={`${ingredientsOpen ? 'flex' : 'hidden'} flex-col px-4 pb-2 md:flex`}
                data-testid="cook-ingredients-list"
              >
                {recipe.ingredients.map((ing) => {
                  const display = scaleIngredientForDisplay(
                    ensureStructured(ing),
                    factor,
                    toSystem,
                  );
                  const isChecked = checked.has(ing.id);
                  return (
                    <button
                      key={ing.id}
                      type="button"
                      data-testid="cook-ingredient-row"
                      data-checked={isChecked}
                      onClick={() => toggleIngredient(ing.id)}
                      className={`flex min-h-12 items-center gap-3 border-t border-line py-2 text-left ${
                        isChecked ? 'opacity-40' : ''
                      }`}
                    >
                      <span
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 ${
                          isChecked ? 'border-fg-3 bg-fg-3' : 'border-line-strong'
                        }`}
                      >
                        {isChecked && <Check size={16} className="text-surface" strokeWidth={3} />}
                      </span>
                      <span
                        className={`flex-1 text-[15px] text-fg ${isChecked ? 'line-through' : ''}`}
                      >
                        {display.text}
                      </span>
                    </button>
                  );
                })}
                {recipe.ingredients.length === 0 && (
                  <p className="py-4 text-center text-[13px] text-fg-3">No ingredients listed.</p>
                )}
              </div>
            </section>

            <StepsList
              steps={steps}
              currentIndex={clampedStepIndex}
              onIndexChange={setStepIndex}
              timers={timersState.timers}
              now={now}
              onChip={handleChip}
              onCookedThis={user ? finishAndLogCook : undefined}
            />
          </div>
        </div>
      </div>

      {steps.length > 0 && (
        <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-line bg-surface px-4 py-3 pb-[max(12px,env(safe-area-inset-bottom))]">
          <button
            type="button"
            aria-label="Previous step"
            data-testid="cook-step-prev"
            disabled={clampedStepIndex === 0}
            onClick={() => setStepIndex(clampedStepIndex - 1)}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border-2 border-line-strong text-fg disabled:opacity-30"
          >
            <ChevronLeft size={22} />
          </button>
          <span
            className="font-mono text-[13px] font-semibold text-fg-2"
            data-testid="cook-step-position"
          >
            Step {clampedStepIndex + 1} of {steps.length}
          </span>
          <button
            type="button"
            aria-label="Next step"
            data-testid="cook-step-next"
            disabled={clampedStepIndex === steps.length - 1}
            onClick={() => setStepIndex(clampedStepIndex + 1)}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border-2 border-line-strong text-fg disabled:opacity-30"
          >
            <ChevronRight size={22} />
          </button>
        </div>
      )}

      {exitConfirm && (
        <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center">
          <div className="w-full max-w-sm rounded-t-xl bg-surface p-5 sm:rounded-xl">
            <p className="text-[16px] font-bold text-fg">Timers still running</p>
            <p className="mt-1 text-[14px] text-fg-3">
              Leaving cook mode won&apos;t stop them, but you won&apos;t see the countdown or get
              the chime until you come back.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                data-testid="exit-confirm-cancel"
                onClick={() => setExitConfirm(false)}
                className="h-11 flex-1 rounded-md border-2 border-line-strong text-[14px] font-semibold text-fg"
              >
                Keep cooking
              </button>
              <button
                type="button"
                data-testid="exit-confirm-leave"
                onClick={() => {
                  setExitConfirm(false);
                  navigate(`/recipes/${id}`);
                }}
                className="h-11 flex-1 rounded-md bg-inverse text-[14px] font-semibold text-inverse-fg"
              >
                Leave anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_STEPS: string[] = [];

/**
 * One pinned timer. The countdown is the biggest thing in the row — it's read from arm's
 * length with flour on your hands. A finished timer stays (quietly, "Done") until it's
 * dismissed, so a chime you half-heard can be confirmed at a glance; nothing flashes.
 */
function TimerRow({
  timer,
  now,
  onPause,
  onResume,
  onCancel,
}: {
  timer: CookTimer;
  now: number;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
}): JSX.Element {
  const done = timer.status === 'done';
  const seconds = remainingSeconds(timer, now);
  return (
    <div
      className={`flex min-h-14 items-center gap-2 rounded-md border-l-4 bg-surface py-1.5 pl-3 pr-1 ${
        timer.status === 'running' ? 'border-accent' : 'border-line-strong'
      }`}
      data-testid="timer-row"
      data-timer-status={timer.status}
    >
      <Timer size={18} aria-hidden="true" className="shrink-0 text-fg-3" />
      <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-fg-2">
        {timer.name}
      </span>
      {done ? (
        <span className="shrink-0 text-[15px] font-bold text-fg">Done</span>
      ) : (
        <span
          className={`shrink-0 font-mono text-[24px] font-bold tabular-nums leading-none ${
            timer.status === 'paused' ? 'text-fg-3' : 'text-fg'
          }`}
          data-testid="timer-countdown"
        >
          {formatCountdown(seconds)}
        </span>
      )}
      {!done && (
        <button
          type="button"
          aria-label={timer.status === 'running' ? 'Pause timer' : 'Resume timer'}
          data-testid="timer-pause-resume"
          onClick={timer.status === 'running' ? onPause : onResume}
          className="flex h-11 w-11 shrink-0 items-center justify-center text-fg-2"
        >
          {timer.status === 'running' ? <Pause size={18} /> : <Play size={18} />}
        </button>
      )}
      <button
        type="button"
        aria-label={done ? 'Dismiss timer' : 'Cancel timer'}
        data-testid="timer-cancel"
        onClick={onCancel}
        className="flex h-11 w-11 shrink-0 items-center justify-center text-fg-2"
      >
        <X size={18} />
      </button>
    </div>
  );
}

/**
 * Every step, in order, with the current one called out (bigger type, accent rail) and the
 * ones behind it dimmed. Tapping a step makes it current; the footer arrows step through.
 * Durations in any step are timer chips — see `handleChip` in `CookModePage`.
 */
function StepsList({
  steps,
  currentIndex,
  onIndexChange,
  timers,
  now,
  onChip,
  onCookedThis,
}: {
  steps: string[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  timers: CookTimer[];
  now: number;
  onChip: (stepNum: number, label: string, seconds: number) => void;
  /** Absent when nobody is signed in — a cook has to have somebody's name on it. */
  onCookedThis?: () => void;
}): JSX.Element {
  const currentRef = useRef<HTMLDivElement | null>(null);
  const firstRender = useRef(true);

  // Keep the current step on screen when the arrows (or a summary fact) move it. Skipped on
  // first render at step 1 so opening cook mode lands on the summary card, not mid-page.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      if (currentIndex === 0) return;
    }
    currentRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [currentIndex]);

  if (steps.length === 0) {
    return (
      <section
        className="px-1 py-4 text-center text-[13px] text-fg-3"
        data-testid="cook-steps-panel"
      >
        No steps listed.
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-2" data-testid="cook-steps-panel" aria-label="Steps">
      <span className="px-1 text-[11px] font-semibold uppercase tracking-wider text-fg-3">
        Steps · {steps.length}
      </span>
      {steps.map((step, i) => {
        const isCurrent = i === currentIndex;
        const durations = parseDurations(step);
        return (
          // A `div[role=button]`, not a real `<button>` — it hosts the duration chips, which
          // are themselves real buttons, and nesting `<button>` inside `<button>` is invalid
          // HTML (the browser silently closes the outer one, breaking the tap target).
          <div
            key={i}
            ref={isCurrent ? currentRef : undefined}
            role="button"
            tabIndex={0}
            aria-current={isCurrent ? 'step' : undefined}
            data-testid={isCurrent ? 'cook-current-step' : 'cook-step'}
            onClick={() => onIndexChange(i)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onIndexChange(i);
              }
            }}
            className={`flex scroll-my-3 gap-3 rounded-lg border-l-4 px-3 py-3 text-left ${
              isCurrent
                ? 'border-accent bg-surface shadow-sm'
                : i < currentIndex
                  ? 'border-transparent opacity-50'
                  : 'border-transparent'
            }`}
          >
            <span
              className={`shrink-0 pt-0.5 font-mono text-[13px] font-semibold ${
                isCurrent ? 'text-accent' : 'text-fg-3'
              }`}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-2.5">
              <p
                className={
                  isCurrent
                    ? 'text-[20px] font-semibold leading-snug text-fg'
                    : 'text-[16px] leading-relaxed text-fg-2'
                }
              >
                {step}
              </p>
              {durations.length > 0 && (
                <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                  {durations.map((d) => {
                    const timer = timers.find((t) => t.name === timerNameFor(i + 1, d.label));
                    return (
                      <TimerChip
                        key={`${d.start}-${d.end}`}
                        label={d.label}
                        timer={timer}
                        now={now}
                        onClick={() => onChip(i + 1, d.label, d.seconds)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {onCookedThis && (
        // Under the last step: the one thing left to do once the steps run out.
        <button
          type="button"
          data-testid="cook-cooked-this"
          onClick={onCookedThis}
          className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-md bg-inverse text-[15px] font-semibold text-inverse-fg"
        >
          <CookingPot size={18} aria-hidden="true" />
          Cooked this
        </button>
      )}
    </section>
  );
}

/** A step's duration as a timer. Idle: a solid "Start 10 min" pill. Running/paused: shows
 * the live countdown and pauses/resumes. Done: says so, and a tap starts it fresh. */
function TimerChip({
  label,
  timer,
  now,
  onClick,
}: {
  label: string;
  timer: CookTimer | undefined;
  now: number;
  onClick: () => void;
}): JSX.Element {
  const status = timer?.status ?? 'idle';
  const base =
    'flex h-11 items-center gap-2 rounded-full px-4 text-[14px] font-semibold active:opacity-80';
  if (status === 'running' || status === 'paused') {
    return (
      <button
        type="button"
        data-testid="duration-chip"
        data-timer-status={status}
        aria-label={`${label} timer, ${status === 'running' ? 'pause' : 'resume'}`}
        onClick={onClick}
        className={`${base} border-2 border-accent bg-surface text-fg`}
      >
        {status === 'running' ? <Pause size={16} /> : <Play size={16} />}
        <span className="font-mono tabular-nums">
          {formatCountdown(remainingSeconds(timer!, now))}
        </span>
      </button>
    );
  }
  return (
    <button
      type="button"
      data-testid="duration-chip"
      data-timer-status={status}
      onClick={onClick}
      className={`${base} ${
        status === 'done'
          ? 'border-2 border-line-strong bg-surface text-fg-2'
          : 'bg-inverse text-inverse-fg'
      }`}
    >
      {status === 'done' ? <Check size={16} /> : <Timer size={16} />}
      {status === 'done' ? `${label} · done` : `Start ${label}`}
    </button>
  );
}
